import { Box, CircularProgress } from '@mui/material'
import FabButton from 'ui/Element/Button/Fab'
import { getThemeColor } from 'lib/util'
import { useAttachmentAPI, useMemoRef, useStyles } from '@platform/react/hook'
import { useEffect, useMemo, useRef, useCallback } from 'react'

const SIZE = '1.5rem'

const styles = (theme, { color }) => ({
  root: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ring: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 'auto',
    zIndex: 1060,
    '& .MuiCircularProgress-circle': {
      stroke: color ? getThemeColor(theme, color) : theme.palette.black.main,
    },
  },
  track: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 'auto',
    zIndex: 1060,
    '& .MuiCircularProgress-circle': {
      stroke: theme.palette.gray[700],
    },
  },
})

/**
 * AttachmentListTemplate Circle Button
 *
 * Displays a FAB Button that can be clicked to download `attachment`. Will display a
 * circular indicator around the button for the download progress.
 *
 * @param {Object} props
 * @param {Object} props.attachment         the attachment to download
 * @param {Object} props.events             events used in this button
 * @param {boolean} props.download          whether the file can be downloaded
 * @param {Object} [props.events]
 * @param {Function} [props.events.onClick] handler for clicking the button
 * @param {boolean} props.showProgress      whether to show the progress bar or not
 * @param {number} [props.duration=300]     animation duration
 * @param {string} [props.group]            attachment group
 * @param {string} [props.background]       background color of the button
 * @param {string} [props.color=secondary]  color of the icon and the progress ring
 * @param {string} [props.icon=download]    the icon to display on the button
 * @param {number} [props.progress]         externally controlled progress (e.g. an in-flight
 *                                          upload). When set, the button is non-interactive and
 *                                          reflects this progress instead of fetching the
 *                                          attachment and reporting its download progress.
 * @returns {JSX.Element}
 */
const AttachmentListTemplateCircleButton = ({
  attachment,
  events,
  download = true,
  showProgress = true,
  duration = 300,
  group,
  background,
  color = 'secondary',
  icon = 'download',
  progress: progressProp,
}) => {
  const classes = useStyles(styles, { color })()
  const { value: item } = attachment

  const isControlled = progressProp !== undefined

  const link = useRef({
    a: document.createElement('a'),
    fetched: false,
  })

  const { state, fetch } = useAttachmentAPI({
    group,
    attachment,
    initial: false,
  })
  const progress = isControlled ? progressProp : state?.progress ?? 0
  // A controlled/upload button shows the ring the whole time it's uploading — upload progress is
  // unreliable and can jump straight to 100 while the request is still in flight — and falls back
  // to an indeterminate animation whenever there's no meaningful value to show (0 or 100).
  const indeterminate = isControlled && (progress <= 0 || progress >= 100)
  const isLoading = useMemo(
    () => isControlled || (progress > 0 && progress < 100),
    [isControlled, progress],
  )

  useEffect(() => {
    if (state?.src && state?.progress === 100 && state?.loading === false) {
      link.current.a.href = state.src
      link.current.a.download = decodeURIComponent(item.name)
      if (link.current.fetched) {
        download && link.current.a.click()
        link.current.fetched = false
      }
    }
  }, [state])

  const handleClick = useCallback((e) => {
    if (isControlled) return
    if (!state?.src) {
      link.current = { ...link.current, fetched: true }
      fetch()
    } else {
      download && link?.current?.a?.href && link.current.a.click()
      events?.onClick?.({ ...e, detail: { state } })
    }
  }, [state, isControlled])

  return (
    <Box className={classes.root}>
      <FabButton
        size={'small'}
        color={progress === 0 ? color : background}
        background={background}
        events={{ onClick: handleClick }}
        icon={icon}
      />
      {indeterminate && showProgress && (
        <CircularProgress
          value={100}
          size={SIZE}
          duration={duration}
          variant={'determinate'}
          className={classes.track}
        />
      )}
      {isLoading && showProgress && (
        <CircularProgress
          size={SIZE}
          {...!indeterminate && { value: progress }}
          variant={indeterminate ? 'indeterminate' : 'determinate'}
          className={classes.ring}
        />
      )}
    </Box>
  )
}

export default useMemoRef(AttachmentListTemplateCircleButton, props => [
  props.attachment,
  props.group,
  props.progress,
  props.icon,
])
